import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client'; // Only for auth
import { paymentsApi, type ClientTransactionHistoryItem } from '@/api/payments';
import { contractsApi, type ContractWithBids, type WalletWithContracts } from '@/api/contracts';
import { usersApi } from '@/api/users';
import { api } from '@/api/api-client';

// Function to fetch the client data including wallet balance
const fetchClientData = async () => {
  // Get the authenticated user
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error('You must be logged in to view wallet details');
  }

  try {
    // Get the actual client ID via API
    const clientsData = await api<any[]>(`/clients?user_id=${user.id}`);
    const clientData = clientsData[0];

    if (!clientData) {
      // Unverified user with no client profile - return zero balance
      return {
        client_id: '',
        wallet: 0,
      };
    }

    // Use payments API to get wallet balance
    const walletData = await paymentsApi.getClientWallet(user.id);
    
    return {
      client_id: clientData.client_id,
      wallet: walletData.balance || 0,
    };
  } catch (error) {
    console.error('Error fetching client wallet:', error);
    // Return zero balance for unverified users or API errors
    return {
      client_id: '',
      wallet: 0,
    };
  }
};

// Function to fetch client transaction history
export const fetchClientTransactions = async (): Promise<ClientTransactionHistoryItem[]> => {
  // Get the authenticated user
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error('You must be logged in to view transactions');
  }

  try {
    // Use the dedicated payments API endpoint for comprehensive transaction history
    const result = await paymentsApi.getClientTransactionHistory({
      userId: user.id,
      limit: 50, // Default limit
    });

    // If no result (unverified user with no client profile), return empty array
    if (!result) return [];

    // The edge function returns an array directly, not a paginated response
    // We need to handle both formats for backward compatibility
    if (Array.isArray(result)) {
      return result;
    }

    // If it's a paginated response, return the transactions array
    return result.transactions || [];
  } catch (error) {
    console.error('Error fetching client transactions:', error);
    // Return empty array for unverified users or API errors
    return [];
  }
};

// Hook to get client transaction history
export const useClientTransactions = () => {
  return useQuery({
    queryKey: ['clientTransactions'],
    queryFn: fetchClientTransactions,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};



// Hook to get client wallet information
export const useClientWallet = () => {
  return useQuery({
    queryKey: ['clientWallet'],
    queryFn: fetchClientData,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Interface for the payment creation data
interface CreatePaymentParams {
  jobId: string;
  clientId: string | number;
}

// Mutation to handle client payment
export const useCreatePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      clientId,
    }: CreatePaymentParams) => {
      // Use the dedicated payments API endpoint that handles all complex logic
      const result = await paymentsApi.createJobPayment({
        jobId,
        clientId: typeof clientId === 'string' ? clientId : String(clientId),
      });

      return {
        paymentId: result.payment_id,
        amount: result.amount,
      };
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['clientWallet'] });
      queryClient.invalidateQueries({ queryKey: ['clientTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['clientDashboard'] });

      // Invalidate payment-related queries to refresh JobPaymentTab
      queryClient.invalidateQueries({
        queryKey: ['paymentExists', variables.jobId],
      });
      queryClient.invalidateQueries({ queryKey: ['bidAmount'] });
      queryClient.invalidateQueries({
        queryKey: ['paymentDetails', variables.jobId],
      });
      queryClient.invalidateQueries({
        queryKey: ['activeBid', variables.jobId],
      });
    },
  });
};

// Function to get client's signed contracts and committed amounts
const fetchClientSignedContracts = async (): Promise<WalletWithContracts> => {
  // Get the authenticated user
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error('You must be logged in to view contract details');
  }

  // Use contracts API to get wallet with contracts
  return await contractsApi.getClientWalletWithContracts(user.id);
};

// Hook to get client wallet with signed contracts validation
export const useClientWalletWithContracts = () => {
  return useQuery({
    queryKey: ['clientWalletWithContracts'],
    queryFn: fetchClientSignedContracts,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Function to validate if client can sign a new contract
export const validateWalletForContract = (
  walletBalance: number,
  totalCommittedAmount: number,
  newContractAmount: number
): { canSign: boolean; message?: string } => {
  const availableBalance = walletBalance - totalCommittedAmount;

  if (availableBalance < newContractAmount) {
    const shortfall = newContractAmount - availableBalance;
    return {
      canSign: false,
      message: `Insufficient funds. You need £${shortfall.toFixed(2)} more in your wallet. Current available balance: £${availableBalance.toFixed(2)} (Wallet: £${walletBalance.toFixed(2)}, Committed to ongoing jobs: £${totalCommittedAmount.toFixed(2)})`,
    };
  }

  return { canSign: true };
};
